// storybook-check-ignore
import React from 'react';

import { Box, Flex, Icon } from '@opendoor/bricks/core';
import { IconName } from '@opendoor/bricks/core/Icon';
import { colors } from '@opendoor/bricks/theme/novo';

import { useLayoutState } from 'platform/components/layout/Layout';

import { TabLink, TabLinkProps } from './TabLink';

// Semantic links without href p props are a11y violations. See https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1943339236/Accessibility+best+practices+for+frontend+development#Common-anti-patterns-to-look-out-for-in-progress
export interface NovoNavTabProps extends Omit<TabLinkProps, 'analyticsName'> {
  /** Name used for metrics only. */
  name: string;
  iconName?: IconName;
  isActive: boolean;
  children: string;
}

/** Dispays the main navigation on mobile and desktop */
export const NovoNavTab: React.FC<NovoNavTabProps> = ({
  name,
  iconName,
  isActive,
  to,
  children,
  'aria-label': ariaLabel,
  ...props
}) => {
  const state = useLayoutState();

  return (
    <TabLink
      to={to}
      variant="app-nav"
      display="flex"
      justifyContent="flex-start"
      isActive={isActive}
      px={[0, 3, 3, 5]}
      py={[2, 2, 4, 4]}
      fontWeight="medium"
      width="auto"
      height={['64px', '64px', '74px', 'auto']}
      analyticsName={`cake-${name}-nav-tab`}
      aria-label={ariaLabel}
      aria-current={isActive ? 'page' : undefined}
      title={state === 'tablet' && ariaLabel ? ariaLabel : undefined}
      data-testid={`${name}-nav-tab`}
      // override the primer.css hover styling for links
      css={{
        color: '#A3A2A0',
        fontSize: '18px !important',
        lineHeight: '120%',
        letterSpacing: '-0.432px',
        '&.active': {
          color: `${colors.neutrals100} !important`,
          backgroundColor: 'transparent !important',
        },
        '&:hover': {
          color: isActive ? colors.neutrals100 : 'inherit',
          backgroundColor: state === 'mobile' ? 'transparent' : colors.neutrals10,
        },
      }}
      {...props}
    >
      <Flex
        display="flex"
        flexDirection={['column', 'column', 'column', 'row']}
        justifyContent="center"
        alignItems="center"
      >
        {iconName && <TabIcon iconName={iconName} isActive={isActive} />}
        {children}
      </Flex>
    </TabLink>
  );
};

const TabIcon: React.FC<{
  iconName: IconName;
  isActive: boolean;
}> = ({ iconName, isActive }) => (
  <Box mb={[1, 1, 1, 0]} mr={[0, 0, 0, 6]} lineHeight="100">
    <Icon name={iconName} color={isActive ? 'brand50' : 'neutrals100'} aria-hidden="true" />
  </Box>
);
